import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeviceAdsService from "../../../services/OrderingServices/DeviceAdsService";

const DeviceAdsList = () => {
  const navigate = useNavigate();
  const { macId: paramMacId } = useParams();
  const [macId, setMacId] = useState(paramMacId || "");
  const [adsList, setAdsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [selectedAdTitle, setSelectedAdTitle] = useState("");

  const handleSearch = () => {
    if (macId.trim()) {
      fetchDeviceAds();
      setSearched(true);
    } else {
      toast.error("Please enter a MAC ID");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  async function fetchDeviceAds() {
    try {
      setLoading(true);
      const response = await DeviceAdsService.getAdsByMacId(macId);
      if (response) {
        setAdsList(Array.isArray(response) ? response : [response]);
        toast.success(
          `Found ${Array.isArray(response) ? response.length : 1} ad(s)`,
        );
      }
    } catch (error) {
      toast.error("Failed to fetch device ads");
      console.error(error);
      setAdsList([]);
    } finally {
      setLoading(false);
    }
  }

  // Auto-fetch if macId is in params
  useEffect(() => {
    if (paramMacId && paramMacId.trim()) {
      setMacId(paramMacId);
      fetchDeviceAds();
      setSearched(true);
    }
  }, [paramMacId]);

  // Auto search when macId changes (with debounce)
  useEffect(() => {
    if (!macId.trim()) {
      setAdsList([]);
      setSearched(false);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchDeviceAds();
      setSearched(true);
    }, 500); // wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
  }, [macId]);

  const getBaseUrl = () => {
    const currentUrl = window.location.href;
    if (currentUrl.includes("localhost")) {
      return "http://localhost:3000";
    }
    return "https://api-databasewebservice.danfesolution.com";
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${getBaseUrl()}${path}`;
  };

  const handleSetInactiveClick = (adId, adTitle) => {
    setSelectedAdId(adId);
    setSelectedAdTitle(adTitle);
    setIsConfirmModalOpen(true);
  };

  const confirmSetInactive = async () => {
    if (!selectedAdId) return;
    try {
      setLoading(true);
      await DeviceAdsService.setAdInactive(selectedAdId, macId);
      toast.success("Ad set to inactive successfully");
      // Refresh the ads list
      await fetchDeviceAds();
      setIsConfirmModalOpen(false);
      setSelectedAdId(null);
      setSelectedAdTitle("");
    } catch (error) {
      toast.error("Failed to set ad as inactive");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const cancelSetInactive = () => {
    setIsConfirmModalOpen(false);
    setSelectedAdId(null);
    setSelectedAdTitle("");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Device Ads" pageTitle="Ads Management" />

          {/* Search Section */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardHeader className="bg-light">
                  <h5 className="mb-0">Search Device Ads by MAC ID</h5>
                </CardHeader>
                <CardBody>
                  <Row className="align-items-end">
                    <Col lg="4">
                      <FormGroup>
                        <Label for="macId" className="form-label">
                          MAC ID <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="macId"
                          placeholder="Enter MAC ID (e.g., 145D346BDE3F)"
                          value={macId}
                          onChange={(e) => setMacId(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={loading}
                        />
                      </FormGroup>
                    </Col>
                    {/* <Col lg="4">
                      <Button
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading || !macId.trim()}
                        className="w-100"
                      >
                        {loading ? "Searching..." : "Search"}
                      </Button>
                    </Col> */}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Loading State */}
          {loading && (
            <Row className="justify-content-center my-5">
              <Col xs="auto">
                <Triangle
                  height="80"
                  width="80"
                  color="#556ee6"
                  ariaLabel="triangle-loading"
                  visible={true}
                />
              </Col>
            </Row>
          )}

          {/* Ads List - Table View */}
          {!loading && searched && adsList.length > 0 && (
            <Row>
              <Col lg="12">
                <Card>
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">
                      Ads for MAC ID: <Badge color="primary">{macId}</Badge>
                      <span className="ms-2 text-muted">
                        ({adsList.length} ad{adsList.length !== 1 ? "s" : ""})
                      </span>
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <Table hover>
                        <thead>
                          <tr>
                            <th>#</th>
                            {/* <th>Thumbnail</th> */}
                            <th>Title</th>
                            <th>Description</th>
                            <th>Media Type</th>
                            <th>Priority</th>
                            <th>Duration (sec)</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adsList.map((ad) => (
                            <tr key={ad.id}>
                              <td>{ad.id}</td>
                              {/* <td>
                                {ad.thumbnailPath && (
                                  <div
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      overflow: "hidden",
                                      backgroundColor: "#f8f9fa",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(ad.thumbnailPath)}
                                      alt={ad.title}
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  </div>
                                )}
                              </td> */}
                              <td>{ad.title}</td>
                              <td>
                                <span className="text-muted small">
                                  {ad.description || "-"}
                                </span>
                              </td>
                              <td>
                                <Badge color="secondary">
                                  {ad.mediaType === 1 ? "Image" : "Video"}
                                </Badge>
                              </td>
                              <td>
                                <Badge color="info">{ad.priority || "-"}</Badge>
                              </td>
                              <td>{ad.durationSeconds || 0}</td>
                              <td>
                                <Button
                                  size="sm"
                                  color="danger"
                                  outline
                                  onClick={() => handleSetInactiveClick(ad.id, ad.title)}
                                  disabled={loading}
                                >
                                  <i className="ri-close-line"></i> Set Inactive
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* No Results */}
          {!loading && searched && adsList.length === 0 && (
            <Row>
              <Col lg="12">
                <Card className="border-0 bg-light">
                  <CardBody className="text-center py-5">
                    <i
                      className="ri-search-line"
                      style={{ fontSize: "48px", color: "#adb5bd" }}
                    ></i>
                    <p className="text-muted mt-3">
                      No ads found for MAC ID: <strong>{macId}</strong>
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Initial State */}
          {!searched && (
            <Row>
              <Col lg="12">
                <Card className="border-0 bg-light">
                  <CardBody className="text-center py-5">
                    <i
                      className="ri-advertisement-line"
                      style={{ fontSize: "48px", color: "#adb5bd" }}
                    ></i>
                    <p className="text-muted mt-3">
                      Enter a MAC ID and click Search to view device ads
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmModalOpen} toggle={cancelSetInactive}>
        <ModalHeader toggle={cancelSetInactive}>
          Confirm Set Inactive
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to set <strong>"{selectedAdTitle}"</strong> as inactive?
          </p>
          <p className="text-muted small mb-0">
            This action will remove the ad from active rotation.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelSetInactive}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={confirmSetInactive}
            disabled={loading}
          >
            {loading ? "Processing..." : "Yes, Set Inactive"}
          </Button>
        </ModalFooter>
      </Modal>

      <style jsx>{`
        .hover-shadow {
          transition: box-shadow 0.3s ease;
        }
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </React.Fragment>
  );
};

export default DeviceAdsList;
